import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu as MenuIcon, X, LogIn, User, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/rooms", label: "Rooms" },
  { to: "/track", label: "Track Order" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobile, setMobile] = useState(false);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link"><Logo /></Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-amber-300" : "text-zinc-300 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="cart-trigger-btn"
            onClick={() => setOpen(true)}
            className="btn-outline-gold relative !px-3 !py-2"
            aria-label="Cart"
          >
            <ShoppingBag size={18} />
            <span className="hidden sm:inline text-sm">Cart</span>
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-400 text-black text-[11px] font-bold rounded-full min-w-[20px] h-[20px] grid place-items-center px-1">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {user.role === "owner" && (
                <button data-testid="owner-dashboard-nav" onClick={() => nav("/owner")} className="btn-outline-gold !px-3 !py-2">
                  <LayoutDashboard size={16} /> <span className="text-sm">Dashboard</span>
                </button>
              )}
              <button data-testid="account-nav" onClick={() => nav("/account")} className="btn-outline-gold !px-3 !py-2">
                <User size={16} />
              </button>
              <button data-testid="logout-btn" onClick={logout} className="btn-outline-gold !px-3 !py-2" title="Logout">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button data-testid="login-btn" onClick={handleLogin} className="hidden md:inline-flex btn-gold !px-4 !py-2 text-sm">
              <LogIn size={16} /> Sign in
            </button>
          )}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((v) => !v)}
            className="md:hidden btn-outline-gold !px-3 !py-2"
            aria-label="Menu"
          >
            {mobile ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden border-t border-amber-500/15 bg-zinc-950/95 px-4 py-4">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setMobile(false)}
                className={({ isActive }) => `py-2 ${isActive ? "text-amber-300" : "text-zinc-200"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="divider-gold my-2" />
            {user ? (
              <>
                {user.role === "owner" && (
                  <button onClick={() => { setMobile(false); nav("/owner"); }} className="btn-outline-gold justify-start">Owner Dashboard</button>
                )}
                <button onClick={() => { setMobile(false); nav("/account"); }} className="btn-outline-gold justify-start">My Account</button>
                <button onClick={async () => { setMobile(false); await logout(); }} className="btn-outline-gold justify-start">Logout</button>
              </>
            ) : (
              <button onClick={handleLogin} className="btn-gold">Sign in with Google</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
